import React, { useState } from "react";
import api from "../lib/api";
import { ShieldAlert, Loader2, ExternalLink, EyeOff, Search } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-indigo-500/60";

// Finds the published posts on a WordPress site that are likely to be holding
// back AdSense approval, and takes the chosen ones off the live site.
export const AdsenseCheck = ({ sites }) => {
  const [siteId, setSiteId] = useState("");
  const [level, setLevel] = useState("standard");
  const [maxPosts, setMaxPosts] = useState(200);
  const [thin, setThin] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [picked, setPicked] = useState({});
  const [moving, setMoving] = useState(false);
  const [filter, setFilter] = useState("all");

  if (!sites.length) return null;
  const site = siteId || sites[0].id;

  const scan = async () => {
    setScanning(true); setResult(null); setPicked({});
    try {
      const { data } = await api.post(`/wp-sites/${site}/adsense-scan`,
        { level, max_posts: Number(maxPosts), thin_words: thin ? 300 : 0 });
      setResult(data);
      // Policy problems are pre-selected; thin posts are left to judgement.
      setPicked(Object.fromEntries(data.flagged.filter((f) => f.severity === "high").map((f) => [f.id, true])));
      if (!data.flagged.length) toast.success(`Checked ${data.scanned} posts - nothing flagged`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the site's posts", { duration: 14000 });
    } finally { setScanning(false); }
  };

  const ids = Object.keys(picked).filter((k) => picked[k]).map(Number);
  const moveToDraft = async () => {
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} post${ids.length === 1 ? "" : "s"} to draft? They will no longer be visible on the site.`)) return;
    setMoving(true);
    try {
      const { data } = await api.post(`/wp-sites/${site}/posts/status`, { ids, status: "draft" });
      if (data.updated.length) toast.success(`${data.updated.length} post${data.updated.length === 1 ? "" : "s"} moved to draft`);
      if (data.failed.length) toast.error(`${data.failed.length} could not be changed: ${data.failed[0].error}`, { duration: 16000 });
      const done = new Set(data.updated);
      setResult((r) => ({ ...r, flagged: r.flagged.filter((f) => !done.has(f.id)) }));
      setPicked((p) => Object.fromEntries(Object.entries(p).filter(([k]) => !done.has(Number(k)))));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change the posts");
    } finally { setMoving(false); }
  };

  const shown = (result?.flagged || []).filter((f) => filter === "all" || f.severity === filter);
  const allShown = shown.length > 0 && shown.every((f) => picked[f.id]);

  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="adsense-check">
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-300" />
        <h3 className="font-heading font-bold text-slate-100">AdSense check</h3>
      </div>
      <p className="text-xs text-slate-500 max-w-3xl">
        Reads the site's published posts and lists the ones likely to break AdSense policy: restricted
        topics (sexual content, sexual violence, gore, suicide, hard drugs; strict adds killings, weapons,
        gambling), writer's notes left in the text, AI boilerplate, and thin posts. Nothing changes until
        you move posts to draft - which you can undo in WordPress.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        {sites.length > 1 && (
          <select value={site} onChange={(e) => setSiteId(e.target.value)} className={inputCls}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <select value={level} onChange={(e) => setLevel(e.target.value)} className={inputCls}>
          <option value="standard">Standard</option>
          <option value="strict">Strict</option>
        </select>
        <select value={maxPosts} onChange={(e) => setMaxPosts(e.target.value)} className={inputCls}>
          {[100, 200, 500, 1000].map((n) => <option key={n} value={n}>Latest {n} posts</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={thin} onChange={(e) => setThin(e.target.checked)} />
          Flag thin posts (under 300 words)
        </label>
        <button onClick={scan} disabled={scanning} data-testid="adsense-scan"
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold disabled:opacity-60">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {scanning ? "Checking…" : "Check posts"}
        </button>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-400">
              {result.flagged.length} of {result.scanned} posts flagged{result.more ? " (more posts not checked)" : ""}
            </span>
            {Object.entries(result.summary || {}).map(([label, n]) => (
              <span key={label} className="px-2 py-0.5 rounded-full border border-slate-800 text-slate-400">{label} · {n}</span>
            ))}
          </div>
          {result.flagged.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {[["all", "All"], ["high", "Policy risks"], ["low", "Thin only"]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${filter === k
                    ? "border-indigo-400/60 bg-indigo-500/15 text-indigo-100" : "border-slate-800 text-slate-400"}`}>{l}</button>
              ))}
              <button onClick={() => setPicked((p) => ({ ...p, ...Object.fromEntries(shown.map((f) => [f.id, !allShown])) }))}
                className="text-xs font-semibold text-slate-400 hover:text-slate-200 ml-2">
                {allShown ? "Clear selection" : "Select all shown"}
              </button>
              <button onClick={moveToDraft} disabled={!ids.length || moving} data-testid="adsense-draft"
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold disabled:opacity-50">
                {moving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
                Move {ids.length || ""} to draft
              </button>
            </div>
          )}
          <div className="space-y-2 max-h-[32rem] overflow-y-auto pr-1">
            {shown.map((f) => (
              <label key={f.id} className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${picked[f.id]
                ? "border-rose-500/40 bg-rose-500/5" : "border-slate-800"}`}>
                <input type="checkbox" className="mt-1" checked={!!picked[f.id]}
                  onChange={(e) => setPicked((p) => ({ ...p, [f.id]: e.target.checked }))} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <p className="text-sm text-slate-100 font-semibold leading-snug flex-1">{f.title || `Post ${f.id}`}</p>
                    <a href={f.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      className="text-slate-500 hover:text-slate-300 shrink-0"><ExternalLink className="w-3.5 h-3.5" /></a>
                  </div>
                  <p className="text-[11px] text-slate-600">{(f.date || "").slice(0, 10)}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {f.reasons.map((r, i) => (
                      <span key={i} className={`px-2 py-0.5 rounded-full text-[11px] border ${r.kind === "quality"
                        ? "border-slate-700 text-slate-400" : "border-amber-500/40 text-amber-200"}`}>
                        {r.label}{r.terms?.length ? `: ${r.terms.slice(0, 3).join(", ")}` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdsenseCheck;
